import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Campaign Detail Performance Tab
export const CampaignPerformanceTab = ({ campaignId }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [diagnosis, setDiagnosis] = useState(null);
  const [verification, setVerification] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!campaignId) return;
    const fetch = async () => {
      try {
        const [snapRes, diagRes, verRes, riskRes, scaleRes] = await Promise.all([
          axios.get(`${API}/performance/snapshots/campaign/${campaignId}`),
          axios.get(`${API}/performance/campaigns/diagnosis/${campaignId}`).catch(() => null),
          axios.get(`${API}/performance/campaigns/verification/${campaignId}`).catch(() => null),
          axios.get(`${API}/performance/campaigns/at-risk`),
          axios.get(`${API}/performance/campaigns/scale-candidates`),
        ]);
        const snaps = (snapRes.data.snapshots || snapRes.data || []).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        setSnapshots(snaps);
        if (diagRes?.data) setDiagnosis(diagRes.data);
        if (verRes?.data) setVerification(verRes.data);

        // Check health status
        const isAtRisk = (riskRes.data.campaigns || []).find(c => c.campaign_id === campaignId);
        const isScale = (scaleRes.data.campaigns || []).find(c => c.campaign_id === campaignId);
        if (isAtRisk) setHealthStatus({ type: 'AT_RISK', data: isAtRisk });
        else if (isScale) setHealthStatus({ type: 'SCALE_CANDIDATE', data: isScale });
        else setHealthStatus({ type: 'HEALTHY', data: null });
      } catch (e) {
        console.error('Failed to load performance data:', e);
      } finally { setLoading(false); }
    };
    fetch();
  }, [campaignId]);

  const chartData = useMemo(() => snapshots.map(s => ({
    date: new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    spend: s.spend,
    revenue: s.revenue,
    profit: s.revenue - s.spend,
    cpa: s.cpa,
    cpc: s.cpc,
    cpm: s.cpm,
    ctr: s.ctr,
    watch_time: s.video_watch_time_avg || 0,
    thumb_stop: s.thumb_stop_rate || 0,
    hook_retention: s.hook_retention_3s || 0,
  })), [snapshots]);

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
    </div>
  );

  if (snapshots.length === 0) return (
    <div className="text-center text-slate-500 py-12">No performance data available for this campaign</div>
  );

  return (
    <div className="space-y-6" data-testid="performance-tab">
      {/* Health Status Badge */}
      <div className="flex items-center gap-4 flex-wrap">
        {healthStatus?.type === 'AT_RISK' && (
          <div className="px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="health-badge-at-risk">
            <span className="text-red-400 font-medium">AT RISK</span>
            <span className="text-red-300/70 text-sm ml-2">
              {healthStatus.data.consecutive_deterioration_days} days deteriorating
            </span>
          </div>
        )}
        {healthStatus?.type === 'SCALE_CANDIDATE' && (
          <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg" data-testid="health-badge-scale">
            <span className="text-emerald-400 font-medium">SCALE CANDIDATE</span>
            <span className="text-emerald-300/70 text-sm ml-2">
              {healthStatus.data.consecutive_improvement_days} days improving
            </span>
          </div>
        )}
        {healthStatus?.type === 'HEALTHY' && (
          <div className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg" data-testid="health-badge-healthy">
            <span className="text-slate-300 font-medium">HEALTHY</span>
          </div>
        )}
        {diagnosis && diagnosis.failure_mode !== 'HEALTHY' && (
          <div className={`px-4 py-2 rounded-lg ${
            diagnosis.failure_mode === 'CREATIVE_FAILING' ? 'bg-purple-500/10 border border-purple-500/30' :
            diagnosis.failure_mode === 'BACKEND_FAILING' ? 'bg-orange-500/10 border border-orange-500/30' :
            'bg-slate-800/50 border border-slate-700'
          }`} data-testid="failure-mode-badge">
            <span className={
              diagnosis.failure_mode === 'CREATIVE_FAILING' ? 'text-purple-400' :
              diagnosis.failure_mode === 'BACKEND_FAILING' ? 'text-orange-400' : 'text-slate-300'
            }>
              {diagnosis.failure_mode?.replace('_', ' ')}
            </span>
            <span className="text-slate-400 text-sm ml-2">{diagnosis.recommended_action}</span>
          </div>
        )}
      </div>

      {/* Spend / Revenue / Profit Chart */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h4 className="text-white font-semibold mb-4">Spend / Revenue / Profit</h4>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#252218" />
            <XAxis dataKey="date" stroke="#9E9880" fontSize={12} />
            <YAxis stroke="#9E9880" fontSize={12} />
            <Tooltip contentStyle={{ backgroundColor: '#111008', border: '1px solid #352F24', borderRadius: 8 }} />
            <Legend />
            <Line type="monotone" dataKey="spend" stroke="#f87171" strokeWidth={2} dot={false} name="Spend" />
            <Line type="monotone" dataKey="revenue" stroke="#FCD34D" strokeWidth={2} dot={false} name="Revenue" />
            <Line type="monotone" dataKey="profit" stroke="#60a5fa" strokeWidth={2} dot={false} name="Profit" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* CPA / CPC / CPM Trends */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h4 className="text-white font-semibold mb-4">CPA / CPC / CPM Trends</h4>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#252218" />
            <XAxis dataKey="date" stroke="#9E9880" fontSize={12} />
            <YAxis stroke="#9E9880" fontSize={12} />
            <Tooltip contentStyle={{ backgroundColor: '#111008', border: '1px solid #352F24', borderRadius: 8 }} />
            <Legend />
            <Line type="monotone" dataKey="cpa" stroke="#fbbf24" strokeWidth={2} dot={false} name="CPA ($)" />
            <Line type="monotone" dataKey="cpc" stroke="#a78bfa" strokeWidth={2} dot={false} name="CPC ($)" />
            <Line type="monotone" dataKey="cpm" stroke="#fb923c" strokeWidth={2} dot={false} name="CPM ($)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Meta Engagement Metrics */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h4 className="text-white font-semibold mb-4">Meta Engagement</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#252218" />
                <XAxis dataKey="date" stroke="#9E9880" fontSize={11} />
                <YAxis stroke="#9E9880" fontSize={11} />
                <Tooltip contentStyle={{ backgroundColor: '#111008', border: '1px solid #352F24', borderRadius: 8 }} />
                <Line type="monotone" dataKey="ctr" stroke="#FCD34D" strokeWidth={2} dot={false} name="CTR %" />
                <Line type="monotone" dataKey="watch_time" stroke="#60a5fa" strokeWidth={2} dot={false} name="Watch Time (s)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#252218" />
                <XAxis dataKey="date" stroke="#9E9880" fontSize={11} />
                <YAxis stroke="#9E9880" fontSize={11} />
                <Tooltip contentStyle={{ backgroundColor: '#111008', border: '1px solid #352F24', borderRadius: 8 }} />
                <Line type="monotone" dataKey="thumb_stop" stroke="#c084fc" strokeWidth={2} dot={false} name="Thumb Stop %" />
                <Line type="monotone" dataKey="hook_retention" stroke="#f472b6" strokeWidth={2} dot={false} name="Hook Retention %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Pre-launch Verification */}
      {verification && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="verification-section">
          <h4 className="text-white font-semibold mb-4">Pre-Launch Verification</h4>
          <div className="flex items-center gap-3 mb-3">
            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
              verification.status === 'PASSED' ? 'bg-emerald-500/20 text-emerald-400' :
              verification.status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
              'bg-amber-500/20 text-amber-400'
            }`}>
              {verification.status}
            </span>
            {verification.destination_url && (
              <span className="text-slate-500 text-sm truncate max-w-md">{verification.destination_url}</span>
            )}
          </div>
          <div className="space-y-2">
            {Object.entries(verification.checks || {}).map(([key, check]) => (
              <div key={key} className="flex items-center gap-3 text-sm">
                <span className={check.passed ? 'text-emerald-400' : 'text-red-400'}>
                  {check.passed ? '✓' : '✗'}
                </span>
                <span className="text-slate-300">{key.replace(/_/g, ' ')}</span>
                {check.error && <span className="text-red-300/70 text-xs">({check.error})</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Audience Segment Intelligence Tab
export const AudienceSegmentsTab = ({ campaignId }) => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!campaignId) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/performance/campaigns/${campaignId}/segments`);
        setSegments(res.data.segments || res.data || []);
      } catch (e) {
        console.error('Failed to load segments:', e);
      } finally { setLoading(false); }
    };
    fetch();
  }, [campaignId]);

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
    </div>
  );

  if (segments.length === 0) return (
    <div className="text-center text-slate-500 py-12">No audience segment data available</div>
  );

  // Group segments by type
  const groupedSegments = {};
  segments.forEach(seg => {
    const type = seg.segment_type || seg.dimension || 'Other';
    if (!groupedSegments[type]) groupedSegments[type] = [];
    groupedSegments[type].push(seg);
  });

  // Calculate campaign averages for delta
  const totalSpend = segments.reduce((s, seg) => s + (seg.spend || 0), 0);
  const totalConv = segments.reduce((s, seg) => s + (seg.conversions || 0), 0);
  const avgCpa = totalSpend / Math.max(totalConv, 1);

  return (
    <div className="space-y-6" data-testid="audience-tab">
      {Object.entries(groupedSegments).map(([type, segs]) => (
        <div key={type} className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <h4 className="text-white font-semibold mb-4 capitalize">{type}</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-slate-400 py-2 px-3">Segment</th>
                  <th className="text-right text-slate-400 py-2 px-3">CPA</th>
                  <th className="text-right text-slate-400 py-2 px-3">CTR</th>
                  <th className="text-right text-slate-400 py-2 px-3">Conversions</th>
                  <th className="text-right text-slate-400 py-2 px-3">Spend</th>
                  <th className="text-right text-slate-400 py-2 px-3">Delta vs Avg</th>
                  <th className="text-right text-slate-400 py-2 px-3">Signal</th>
                </tr>
              </thead>
              <tbody>
                {segs.map((seg, i) => {
                  const segCpa = seg.spend / Math.max(seg.conversions, 1);
                  const deltaPct = avgCpa > 0 ? ((segCpa - avgCpa) / avgCpa * 100) : 0;
                  const isOutperforming = deltaPct < -10;
                  const isUnderperforming = deltaPct > 10;
                  const segName = seg.segment_name || seg.segment_value || seg.value || `Segment ${i}`;

                  return (
                    <tr
                      key={i}
                      className={`border-b border-slate-800/50 ${
                        isOutperforming ? 'bg-emerald-500/5' : isUnderperforming ? 'bg-red-500/5' : ''
                      }`}
                      data-testid={`segment-row-${type}-${i}`}
                    >
                      <td className="py-3 px-3 text-slate-200">{segName}</td>
                      <td className="py-3 px-3 text-right text-slate-300">${segCpa.toFixed(2)}</td>
                      <td className="py-3 px-3 text-right text-slate-300">{(seg.ctr || 0).toFixed(2)}%</td>
                      <td className="py-3 px-3 text-right text-slate-300">{seg.conversions || 0}</td>
                      <td className="py-3 px-3 text-right text-slate-300">${(seg.spend || 0).toFixed(0)}</td>
                      <td className={`py-3 px-3 text-right font-medium ${
                        isOutperforming ? 'text-emerald-400' : isUnderperforming ? 'text-red-400' : 'text-slate-400'
                      }`}>
                        {deltaPct > 0 ? '+' : ''}{deltaPct.toFixed(1)}%
                      </td>
                      <td className="py-3 px-3 text-right">
                        {isOutperforming && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded">
                            Isolate
                          </span>
                        )}
                        {isUnderperforming && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                            Exclude
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};
